import { GameEvent } from "./types";
import { loadMuted, saveMuted } from "./storage";

export class Sfx {
  muted = loadMuted();
  private ctx: AudioContext | null = null;

  volume = Number(localStorage.getItem("cave-critter-sfx-vol") ?? "1");
  private outNode: GainNode | null = null;
  private get out() {
    if (!this.outNode) { this.outNode = this.ac.createGain(); this.outNode.gain.value = this.volume; this.outNode.connect(this.ac.destination); }
    return this.outNode;
  }
  setVolume(v: number) { this.volume = v; localStorage.setItem("cave-critter-sfx-vol", String(v)); if (this.outNode) this.outNode.gain.value = v; }

  private get ac() {
    if (!this.ctx) this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (this.ctx.state === "suspended") this.ctx.resume();
    return this.ctx;
  }

  setMuted(v: boolean) { this.muted = v; saveMuted(v); }

  private barks: AudioBuffer[] = [];
  private barksLoading = false;
  private loadBarks() {
    if (this.barksLoading) return;
    this.barksLoading = true;
    ["bark1", "bark2", "bark_double"].forEach((n) =>
      fetch(`/sfx/${n}.wav`).then((r) => r.arrayBuffer()).then((b) => this.ac.decodeAudioData(b)).then((buf) => this.barks.push(buf)).catch(() => {}),
    );
  }

  private realBark(delay: number) {
    if (!this.barks.length) return false;
    const ac = this.ac;
    const buf = this.barks[Math.floor(Math.random() * this.barks.length)];
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 0.9 + Math.random() * 0.25;
    const g = ac.createGain();
    g.gain.value = 0.55;
    src.connect(g).connect(this.out);
    src.start(ac.currentTime + delay);
    return true;
  }

  private tone(freq: number, endFreq: number, dur: number, type: OscillatorType, gain = 0.15, delay = 0) {
    if (this.muted) return;
    const ac = this.ac;
    const o = ac.createOscillator();
    const g = ac.createGain();
    const t0 = ac.currentTime + delay;
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    o.frequency.exponentialRampToValueAtTime(Math.max(20, endFreq), t0 + dur);
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g).connect(this.out);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  }

  private soft(freq: number, endFreq: number, dur: number, type: OscillatorType, gain: number, cutoff: number, delay = 0) {
    if (this.muted) return;
    const ac = this.ac;
    const o = ac.createOscillator();
    const f = ac.createBiquadFilter();
    const g = ac.createGain();
    const t0 = ac.currentTime + delay;
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    o.frequency.exponentialRampToValueAtTime(Math.max(20, endFreq), t0 + dur);
    f.type = "lowpass";
    f.frequency.value = cutoff;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(f).connect(g).connect(this.out);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  }

  private noise(dur: number, gain = 0.2, delay = 0, cutoff = 800, type: BiquadFilterType = "highpass") {
    if (this.muted) return;
    const ac = this.ac;
    const buf = ac.createBuffer(1, Math.max(1, Math.floor(ac.sampleRate * dur)), ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = ac.createBufferSource();
    src.buffer = buf;
    const g = ac.createGain();
    g.gain.value = gain;
    const f = ac.createBiquadFilter();
    f.type = type;
    f.frequency.value = cutoff;
    src.connect(f).connect(g).connect(this.out);
    src.start(ac.currentTime + delay);
  }

  play(e: GameEvent) {
    switch (e) {
      case "roll": this.rollStart(); break;
      case "thud": this.thud(); break;
      case "levelclear": this.stopRoll(); [523, 659, 784, 1046].forEach((f, i) => this.tone(f, f, 0.22, "triangle", 0.15, i * 0.12)); this.happyBark(0.45); break;
      case "ending": this.stopRoll(); [523, 659, 784, 1046, 1318, 1568].forEach((f, i) => this.tone(f, f, 0.35, "triangle", 0.14, i * 0.14)); this.bark(1.1); break;
      case "gameover": this.stopRoll(); [440, 349, 293, 233].forEach((f, i) => this.tone(f, f * 0.98, 0.4, "triangle", 0.15, i * 0.25)); break;
      case "death": this.stopRoll(); this.whimper(); break;
      case "start": this.loadBarks(); this.stopRoll(); this.bark(0); break;
      case "tick": this.soft(880, 880, 0.06, "square", 0.05, 2500); break;
      case "timeout": this.stopRoll(); [330, 262, 196].forEach((f, i) => this.soft(f, f * 0.9, 0.4, "sawtooth", 0.1, 900, i * 0.22)); this.noise(0.8, 0.12, 0.3, 300, "lowpass"); break;
      case "tunnel": this.stopRoll(); [196, 247, 294, 392].forEach((f, i) => this.soft(f, f * 1.01, 0.4, "triangle", 0.1, 1200, i * 0.16)); this.noise(0.6, 0.05, 0, 500, "lowpass"); break;
      case "exit": this.stopRoll(); [784, 988, 1175, 1568, 1976].forEach((f, i) => this.tone(f, f, 0.25, "triangle", 0.13, i * 0.1)); this.bark(0.7); break;
      case "spawn": this.soft(220, 330, 0.18, "triangle", 0.09, 900); this.soft(330, 440, 0.16, "triangle", 0.07, 900, 0.12); break;
      case "merge": [1046, 1318, 1568, 2093].forEach((f, i) => this.tone(f, f * 1.01, 0.18, "sine", 0.14, i * 0.05)); this.noise(0.06, 0.1); break;
      case "explode": this.boom(); break;
      case "fuse": this.hiss(); break;
      case "crack": [0, 0.05, 0.11, 0.2].forEach((off) => { this.noise(0.03, 0.18, off); this.tone(2400 + Math.random() * 800, 1400, 0.05, "square", 0.05, off); }); break;
      case "freezeburst": [1568, 1975, 2349, 2794].forEach((f, i) => this.tone(f, f * 0.98, 0.3, "sine", 0.1, i * 0.06)); this.noise(0.25, 0.08); break;
      case "push": this.soft(160, 90, 0.16, "sine", 0.09, 500); break;
      case "shatter": this.noise(0.16, 0.07, 0, 2200, "lowpass"); this.soft(520, 180, 0.14, "sine", 0.06, 1200); break;
      case "crush": this.noise(0.06, 0.1); if (!this.realBark(0.02)) this.yip(0.02); break;
      case "combo": [784, 988, 1175, 1568, 2093].forEach((f, i) => this.tone(f, f * 1.03, 0.16, "triangle", 0.13, 0.12 + i * 0.07)); break;
      case "wave": [330, 392, 494, 659].forEach((f, i) => this.soft(f, f * 1.02, 0.3, "triangle", 0.12, 1500, i * 0.11)); this.noise(0.4, 0.06, 0.1, 600, "lowpass"); break;
      case "powerup": [660, 880, 1320].forEach((f, i) => this.tone(f, f * 1.02, 0.14, "triangle", 0.12, i * 0.07)); break;
      case "letter": [880, 1320].forEach((f, i) => this.tone(f, f * 1.05, 0.12, "sine", 0.12, i * 0.08)); break;
      case "extra": [523, 659, 784, 1046, 1318].forEach((f, i) => this.tone(f, f, 0.2, "triangle", 0.15, i * 0.09)); break;
      case "power": [392, 523, 659, 784].forEach((f, i) => this.tone(f, f * 1.01, 0.25, "square", 0.08, i * 0.08)); break;
      case "eat": this.noise(0.08, 0.2); this.tone(300, 900, 0.12, "sawtooth", 0.1); break;
    }
  }

  private rolling = 0;
  private rollNode: { src: AudioBufferSourceNode; gain: GainNode } | null = null;

  private stopRoll() {
    this.rolling = 0;
    if (!this.rollNode) return;
    const { src, gain } = this.rollNode;
    const ac = this.ac;
    gain.gain.cancelScheduledValues(ac.currentTime);
    gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), ac.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.08);
    src.stop(ac.currentTime + 0.1);
    this.rollNode = null;
  }

  private rollStart() {
    this.rolling++;
    if (this.muted || this.rollNode) return;
    const ac = this.ac;
    const buf = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < d.length; i++) { last = last * 0.96 + (Math.random() * 2 - 1) * 0.08; d[i] = last; }
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.playbackRate.value = 1.4;
    const f = ac.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = 260;
    f.Q.value = 0.8;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.28, ac.currentTime + 0.05);
    src.connect(f).connect(g).connect(this.out);
    src.start();
    this.rollNode = { src, gain: g };
  }

  private thud() {
    this.rolling = Math.max(0, this.rolling - 1);
    if (this.rolling === 0) this.stopRoll();
    if (this.muted) return;
    this.soft(120, 50, 0.14, "sine", 0.16, 400);
    this.noise(0.04, 0.05, 0, 600, "lowpass");
  }

  private hiss() {
    if (this.muted) return;
    const ac = this.ac;
    const t0 = ac.currentTime;
    const buf = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (0.6 + 0.4 * Math.sin(i / 400));
    const src = ac.createBufferSource();
    src.buffer = buf;
    const f = ac.createBiquadFilter();
    f.type = "highpass";
    f.frequency.value = 3500;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.06, t0);
    g.gain.linearRampToValueAtTime(0.16, t0 + 0.9);
    g.gain.linearRampToValueAtTime(0.0001, t0 + 1.0);
    src.connect(f).connect(g).connect(this.out);
    src.start(t0);
    src.stop(t0 + 1.0);
    [0, 0.25, 0.5, 0.75].forEach((off) => this.tone(1200, 1200, 0.06, "square", 0.05, off));
  }

  private boom() {
    if (this.muted) return;
    const ac = this.ac;
    const t0 = ac.currentTime;
    const buf = ac.createBuffer(1, ac.sampleRate * 0.7, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 2.2);
    const src = ac.createBufferSource();
    src.buffer = buf;
    const f = ac.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.setValueAtTime(1800, t0);
    f.frequency.exponentialRampToValueAtTime(120, t0 + 0.6);
    const g = ac.createGain();
    g.gain.setValueAtTime(0.7, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.7);
    src.connect(f).connect(g).connect(this.out);
    src.start(t0);
    this.tone(90, 30, 0.5, "sine", 0.5);
  }

  private _noiseBuf: AudioBuffer | null = null;
  private get noiseBuf() {
    if (!this._noiseBuf) {
      const ac = this.ac;
      this._noiseBuf = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
      const d = this._noiseBuf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    return this._noiseBuf;
  }

  private barkOnce(t0: number, base: number, len: number, vol: number) {
    const ac = this.ac;
    const out = ac.createGain();
    out.gain.setValueAtTime(0.0001, t0);
    out.gain.exponentialRampToValueAtTime(vol, t0 + 0.018);
    out.gain.setValueAtTime(vol, t0 + len * 0.35);
    out.gain.exponentialRampToValueAtTime(0.001, t0 + len);
    out.connect(this.out);
    // vocal source: pitch "a" → "v": quick rise then fall
    const src = ac.createOscillator();
    src.type = "sawtooth";
    src.frequency.setValueAtTime(base * 0.8, t0);
    src.frequency.exponentialRampToValueAtTime(base * 1.25, t0 + len * 0.18);
    src.frequency.exponentialRampToValueAtTime(base * 0.55, t0 + len);
    const srcG = ac.createGain(); srcG.gain.value = 0.9;
    src.connect(srcG);
    // formants "A" (open mouth) sliding to "V" (closing)
    [[900, 1.6, 1], [1400, 2.2, 0.55], [2600, 3, 0.25]].forEach(([f, q, g]) => {
      const bp = ac.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = q;
      bp.frequency.setValueAtTime(f, t0);
      bp.frequency.exponentialRampToValueAtTime(f * 0.55, t0 + len);
      const fg = ac.createGain(); fg.gain.value = g;
      srcG.connect(bp).connect(fg).connect(out);
    });
    src.start(t0); src.stop(t0 + len + 0.02);
    // breathy attack
    const n = ac.createBufferSource(); n.buffer = this.noiseBuf;
    const nf = ac.createBiquadFilter(); nf.type = "bandpass"; nf.frequency.value = 1800; nf.Q.value = 0.8;
    const ng = ac.createGain();
    ng.gain.setValueAtTime(0.0001, t0); ng.gain.exponentialRampToValueAtTime(vol * 0.5, t0 + 0.01); ng.gain.exponentialRampToValueAtTime(0.001, t0 + 0.06);
    n.connect(nf).connect(ng).connect(this.out);
    n.start(t0); n.stop(t0 + 0.08);
  }

  private yip(delay: number) {
    if (this.muted) return;
    const t0 = this.ac.currentTime + delay;
    const variant = Math.floor(Math.random() * 4);
    const base = 330 * (0.9 + Math.random() * 0.25);
    const vol = 0.3;
    if (variant === 0) this.barkOnce(t0, base, 0.17, vol);
    else if (variant === 1) { this.barkOnce(t0, base, 0.15, vol); this.barkOnce(t0 + 0.2, base * 1.06, 0.15, vol); }
    else if (variant === 2) { this.barkOnce(t0, base * 1.15, 0.13, vol * 0.9); this.barkOnce(t0 + 0.17, base * 1.1, 0.13, vol * 0.9); this.barkOnce(t0 + 0.34, base * 1.2, 0.14, vol); }
    else this.barkOnce(t0, base * 0.85, 0.22, vol);
  }

  pant(times = 5) {
    if (this.muted) return;
    const ac = this.ac;
    for (let i = 0; i < times; i++) {
      const t0 = ac.currentTime + i * 0.42;
      [[0, 1, 1300], [0.21, 0.55, 900]].forEach(([off, g, f]) => {
        const n = ac.createBufferSource(); n.buffer = this.noiseBuf;
        const bp = ac.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = f; bp.Q.value = 0.7;
        const ng = ac.createGain();
        ng.gain.setValueAtTime(0.0001, t0 + off); ng.gain.exponentialRampToValueAtTime(0.09 * g, t0 + off + 0.05); ng.gain.exponentialRampToValueAtTime(0.001, t0 + off + 0.19);
        n.connect(bp).connect(ng).connect(this.out);
        n.start(t0 + off); n.stop(t0 + off + 0.22);
      });
    }
  }

  private bark(delay: number) {
    if (this.muted) return;
    const ac = this.ac;
    [0, 0.13].forEach((off) => {
      const t0 = ac.currentTime + delay + off;
      const o = ac.createOscillator();
      const f = ac.createBiquadFilter();
      const g = ac.createGain();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(320, t0);
      o.frequency.exponentialRampToValueAtTime(620, t0 + 0.03);
      o.frequency.exponentialRampToValueAtTime(240, t0 + 0.11);
      f.type = "lowpass";
      f.frequency.setValueAtTime(1800, t0);
      f.frequency.exponentialRampToValueAtTime(500, t0 + 0.11);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.28, t0 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.12);
      o.connect(f).connect(g).connect(this.out);
      o.start(t0);
      o.stop(t0 + 0.14);
    });
  }

  private whimper() {
    if (this.muted) return;
    const ac = this.ac;
    const base = ac.currentTime;
    // two soft, descending whimpers: "hiii-u … hii-u"
    [[0, 0.55, 1], [0.5, 0.42, 0.85]].forEach(([off, len, k]) => {
      const t0 = base + off;
      const o = ac.createOscillator();
      const lfo = ac.createOscillator();
      const lfoGain = ac.createGain();
      const f = ac.createBiquadFilter();
      const g = ac.createGain();
      o.type = "triangle";
      o.frequency.setValueAtTime(760 * k, t0);
      o.frequency.exponentialRampToValueAtTime(1150 * k, t0 + len * 0.25);
      o.frequency.exponentialRampToValueAtTime(420 * k, t0 + len);
      lfo.frequency.value = 11; lfoGain.gain.value = 22;
      lfo.connect(lfoGain).connect(o.frequency);
      f.type = "lowpass"; f.frequency.value = 2400;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.11, t0 + 0.06);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + len);
      o.connect(f).connect(g).connect(this.out);
      o.start(t0); lfo.start(t0); o.stop(t0 + len + 0.02); lfo.stop(t0 + len + 0.02);
    });
  }

  private happyBark(delay: number) {
    if (!this.barks.length) { this.yip(delay); return; }
    const ac = this.ac;
    const dbl = this.barks[this.barks.length - 1];
    [[0, 1.12], [0.55, 1.2]].forEach(([off, rate]) => {
      const src = ac.createBufferSource();
      src.buffer = dbl; src.playbackRate.value = rate;
      const g = ac.createGain(); g.gain.value = 0.5;
      src.connect(g).connect(this.out);
      src.start(ac.currentTime + delay + off);
    });
  }
}
